import React, { useState } from 'react';

import { Input } from '@dashboard/components/ui/input';
import {
  type CatalogTemplate,
  type MailAudience,
  useAdminEmailTemplateCatalog,
  useAdminEmailTemplateRender,
} from '@dashboard/hooks/queries/useAdminEmailTemplates';
import { cn } from '@dashboard/lib/utils';

import {
  Columns,
  ListButton,
  ListHeading,
  ListSkeleton,
  SidebarHeader,
  SidebarList,
  StagePlaceholder,
  StageSkeleton,
} from './EmailTemplatesLayout';
import PreviewStage, { errorDetails } from './PreviewStage';
import useEmailTemplatesUrl from './useEmailTemplatesUrl';

const AUDIENCES: { key: MailAudience; label: string; recipient: string }[] = [
  { key: 'customer', label: 'To customers', recipient: 'Sam <sam@example.com>' },
  {
    key: 'seller',
    label: 'To sellers',
    recipient: 'Jordan Lee <jordan@acme.example>, owner of Acme Coffee',
  },
  { key: 'staff', label: 'To event staff', recipient: 'Riley <riley@example.com>, door staff' },
];

const GROUP_ORDER = [
  'Orders',
  'Subscriptions',
  'Events',
  'Forms',
  'Abandoned checkouts',
  'Customer portal',
  'Store emails',
  'Sales notifications',
  'Plan and billing',
  'Account',
  'Event staff',
];

const STORE_SENDER = 'Acme Coffee <hello@acme.example>';
const CHECKOUT_PAGE_SENDER = 'Checkout Page <hello@checkoutpage.com>';

const groupRank = (group: string) => {
  const index = GROUP_ORDER.indexOf(group);
  return index === -1 ? GROUP_ORDER.length : index;
};

function groupByAudience(templates: CatalogTemplate[]) {
  return AUDIENCES.map(audience => {
    const inAudience = templates.filter(template => template.audience === audience.key);
    const groups = [...new Set(inAudience.map(template => template.group))].sort(
      (a, b) => groupRank(a) - groupRank(b),
    );
    return {
      ...audience,
      groups: groups.map(name => ({
        name,
        templates: inAudience.filter(template => template.group === name),
      })),
    };
  }).filter(audience => audience.groups.length > 0);
}

function matchesSearch(template: CatalogTemplate, terms: string[]) {
  const text = [
    template.name,
    template.template,
    template.group,
    template.sentWhen,
    ...template.previews.map(preview => preview.variant),
  ]
    .join(' ')
    .toLowerCase();
  return terms.every(term => text.includes(term));
}

export default function TemplatesPanel() {
  const url = useEmailTemplatesUrl();
  const [search, setSearch] = useState('');
  const catalog = useAdminEmailTemplateCatalog();

  const templates = catalog.data ?? [];
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const visible = templates.filter(template => matchesSearch(template, terms));
  const audiences = groupByAudience(visible);
  const firstInList = groupByAudience(templates)[0]?.groups[0]?.templates[0];

  const selected =
    templates.find(template => template.template === url.template) ??
    templates.find(template => template.previews.some(preview => preview.id === url.preview)) ??
    firstInList;
  const preview = selected?.previews.find(item => item.id === url.preview) ?? selected?.previews[0];

  const render = useAdminEmailTemplateRender(preview?.id);

  const selectTemplate = (template: CatalogTemplate) =>
    url.update({ template: template.template, preview: template.previews[0]?.id ?? '' });

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const first = audiences[0]?.groups[0]?.templates[0];
      if (first) selectTemplate(first);
    }
    if (event.key === 'Escape') setSearch('');
  };

  const sidebar = (
    <>
      <SidebarHeader>
        <Input
          type="search"
          aria-label="Search templates"
          placeholder="Search emails, templates or variants"
          autoComplete="off"
          spellCheck={false}
          value={search}
          onChange={event => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        {catalog.data && (
          <p className="text-xs text-muted-foreground">
            {visible.length} of {templates.length} templates
          </p>
        )}
      </SidebarHeader>

      {catalog.isLoading ? (
        <ListSkeleton />
      ) : catalog.error ? (
        <p role="alert" className="p-4 text-sm text-destructive">
          Could not load the templates. {errorDetails(catalog.error).message}
        </p>
      ) : (
        <SidebarList label="Email templates">
          {audiences.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">No emails match "{search}".</p>
          )}
          {audiences.map(audience => (
            <section key={audience.key} aria-labelledby={`email-audience-${audience.key}`}>
              <h2
                id={`email-audience-${audience.key}`}
                className="px-2 pt-4 pb-1 text-sm font-semibold"
              >
                {audience.label}
              </h2>
              {audience.groups.map(group => (
                <div key={group.name}>
                  <ListHeading>{group.name}</ListHeading>
                  {group.templates.map(template => (
                    <ListButton
                      key={template.template}
                      active={template.template === selected?.template}
                      onClick={() => selectTemplate(template)}
                      aside={
                        template.previews.length > 1 && (
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {template.previews.length}
                          </span>
                        )
                      }
                    >
                      <span className="block truncate font-medium">{template.name}</span>
                      <span className="block truncate font-mono text-xs text-muted-foreground">
                        {template.template}
                      </span>
                    </ListButton>
                  ))}
                </div>
              ))}
            </section>
          ))}
        </SidebarList>
      )}
    </>
  );

  if (!selected) {
    return (
      <Columns
        sidebar={sidebar}
        stage={
          catalog.isLoading ? (
            <StageSkeleton />
          ) : (
            <StagePlaceholder title="Pick an email">
              Choose an email on the left to preview it with sample data.
            </StagePlaceholder>
          )
        }
      />
    );
  }

  const audience = AUDIENCES.find(item => item.key === selected.audience);
  const subject = !preview
    ? 'No sample data yet'
    : render.error
      ? 'This email failed to render. The error is below.'
      : (render.data?.subject ?? 'Rendering…');

  const header = (
    <div className="space-y-1">
      <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>{selected.name}</span>
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
          {selected.layout}/{selected.template}.mjml
        </code>
      </p>
      <h2
        className={cn(
          'text-lg leading-snug font-semibold break-words',
          (render.error || !preview) && 'text-destructive',
          render.isLoading && 'text-muted-foreground',
        )}
      >
        {subject}
      </h2>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 pt-1 text-sm">
        <dt className="text-muted-foreground">From</dt>
        <dd className="break-words">
          {selected.layout === 'store' ? STORE_SENDER : CHECKOUT_PAGE_SENDER}
        </dd>
        <dt className="text-muted-foreground">To</dt>
        <dd className="break-words">{audience?.recipient}</dd>
        {selected.sentWhen && (
          <>
            <dt className="text-muted-foreground">Sent when</dt>
            <dd className="break-words">{selected.sentWhen}</dd>
          </>
        )}
      </dl>
    </div>
  );

  return (
    <Columns
      sidebar={sidebar}
      stage={
        <PreviewStage
          title={selected.name}
          header={header}
          tabs={selected.previews.map(item => ({ value: item.id, label: item.variant }))}
          activeTab={preview?.id}
          onTabChange={value => url.update({ template: selected.template, preview: value })}
          html={render.data?.html}
          isLoading={render.isLoading}
          isFetching={render.isFetching}
          error={render.error}
          onReload={() => render.refetch()}
          placeholder={
            !preview && (
              <StagePlaceholder title="No sample for this template">
                Add one in apps/api/src/mail-preview/samples so {selected.template} shows here.
              </StagePlaceholder>
            )
          }
        />
      }
    />
  );
}
